import type { Language } from "@heredajo/schema";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AuthError,
  completeNewPassword,
  confirmPassword,
  confirmSignUp,
  forgotPassword,
  isPendingApprovalError,
  resendConfirmationCode,
  signIn,
  signUp,
} from "../lib/auth.js";
import { t } from "../lib/strings.js";

interface Props {
  lang: Language;
}

type Mode =
  | "signin"
  | "signup"
  | "verify"
  | "approvalPending"
  | "forgot"
  | "reset"
  | "passwordChanged"
  | "newPasswordRequired";

export function Login({ lang }: Props): JSX.Element {
  const s = t(lang);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("return_to") ?? "/";

  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [newPasswordUser, setNewPasswordUser] = useState<CognitoUser | null>(null);

  const reset = () => {
    setError(null);
    setInfo(null);
  };

  const showError = (err: unknown) => {
    if (isPendingApprovalError(err)) {
      setMode("approvalPending");
      reset();
      return;
    }
    if (err instanceof AuthError) setError(err.message);
    else if (err instanceof Error) setError(err.message);
    else setError(String(err));
  };

  const onSignIn = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      const result = await signIn(email, password);
      if (result.kind === "newPasswordRequired" && result.user) {
        setNewPasswordUser(result.user);
        setMode("newPasswordRequired");
      } else {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const onSignUp = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    if (password !== passwordConfirm) {
      setError(s.passwordConfirm);
      return;
    }
    setBusy(true);
    try {
      await signUp(email, password);
      setMode("verify");
      setPassword("");
      setPasswordConfirm("");
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      await confirmSignUp(email, code);
      setMode("approvalPending");
      setCode("");
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    reset();
    setBusy(true);
    try {
      await resendConfirmationCode(email);
      setInfo(s.verificationSentBody(email));
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const onForgotStart = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      await forgotPassword(email);
      setMode("reset");
      setInfo(s.forgotPasswordCodeSent);
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const onResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    if (password !== passwordConfirm) {
      setError(s.passwordConfirm);
      return;
    }
    setBusy(true);
    try {
      await confirmPassword(email, code, password);
      setMode("passwordChanged");
      setPassword("");
      setPasswordConfirm("");
      setCode("");
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const onNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    if (password !== passwordConfirm) {
      setError(s.passwordConfirm);
      return;
    }
    if (!newPasswordUser) {
      setError("session expired");
      return;
    }
    setBusy(true);
    try {
      await completeNewPassword(newPasswordUser, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="bg-white border border-parchment-200 rounded-md shadow-sm p-6">
        {(mode === "signin" || mode === "signup") && (
          <div className="flex border-b border-parchment-200 -mx-6 -mt-6 mb-6">
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                reset();
              }}
              className={`flex-1 px-4 py-3 ${
                mode === "signin"
                  ? "bg-parchment-100 font-semibold"
                  : "text-ink-muted hover:bg-parchment-50"
              }`}
            >
              {s.signInTab}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("signup");
                reset();
              }}
              className={`flex-1 px-4 py-3 border-l border-parchment-200 ${
                mode === "signup"
                  ? "bg-parchment-100 font-semibold"
                  : "text-ink-muted hover:bg-parchment-50"
              }`}
            >
              {s.signUpTab}
            </button>
          </div>
        )}

        {mode === "signin" && (
          <form onSubmit={onSignIn} className="space-y-4">
            <p className="text-sm text-ink-muted">{s.signInPrompt}</p>
            <Field
              label={s.email}
              value={email}
              onChange={setEmail}
              type="email"
              autoFocus
              required
            />
            <Field
              label={s.password}
              value={password}
              onChange={setPassword}
              type="password"
              required
            />
            {error && <p className="text-sm text-red-700">{error}</p>}
            <Submit busy={busy} label={s.signIn} workingLabel={s.workingLabel} />
            <button
              type="button"
              onClick={() => {
                setMode("forgot");
                reset();
              }}
              className="text-sm text-ink-muted hover:text-ink underline"
            >
              {s.forgotPassword}
            </button>
          </form>
        )}

        {mode === "signup" && (
          <form onSubmit={onSignUp} className="space-y-4">
            <p className="text-sm text-ink-muted">{s.signUpPrompt}</p>
            <Field
              label={s.email}
              value={email}
              onChange={setEmail}
              type="email"
              autoFocus
              required
            />
            <Field
              label={s.password}
              value={password}
              onChange={setPassword}
              type="password"
              required
              hint={s.passwordMinHint}
            />
            <Field
              label={s.passwordConfirm}
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              type="password"
              required
            />
            {error && <p className="text-sm text-red-700">{error}</p>}
            <Submit busy={busy} label={s.signUp} workingLabel={s.workingLabel} />
          </form>
        )}

        {mode === "verify" && (
          <form onSubmit={onConfirm} className="space-y-4">
            <h2 className="font-serif text-xl">{s.verificationSentTitle}</h2>
            <p className="text-sm text-ink-muted">{s.verificationSentBody(email)}</p>
            <Field
              label={s.verificationCode}
              value={code}
              onChange={setCode}
              type="text"
              autoFocus
              required
            />
            {error && <p className="text-sm text-red-700">{error}</p>}
            {info && <p className="text-sm text-green-700">{info}</p>}
            <Submit busy={busy} label={s.signUp} workingLabel={s.workingLabel} />
            <button
              type="button"
              onClick={onResend}
              className="text-sm text-ink-muted hover:text-ink underline"
            >
              {s.resendCode}
            </button>
          </form>
        )}

        {mode === "approvalPending" && (
          <div className="space-y-4">
            <h2 className="font-serif text-xl">{s.pendingApprovalTitle}</h2>
            <p className="text-sm text-ink-muted">{s.pendingApprovalBody}</p>
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                reset();
              }}
              className="text-sm text-ink-muted hover:text-ink underline"
            >
              {s.backToSignIn}
            </button>
          </div>
        )}

        {mode === "forgot" && (
          <form onSubmit={onForgotStart} className="space-y-4">
            <h2 className="font-serif text-xl">{s.resetPassword}</h2>
            <p className="text-sm text-ink-muted">{s.forgotPasswordPrompt}</p>
            <Field
              label={s.email}
              value={email}
              onChange={setEmail}
              type="email"
              autoFocus
              required
            />
            {error && <p className="text-sm text-red-700">{error}</p>}
            <Submit busy={busy} label={s.resetPassword} workingLabel={s.workingLabel} />
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                reset();
              }}
              className="text-sm text-ink-muted hover:text-ink underline"
            >
              {s.backToSignIn}
            </button>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={onResetPassword} className="space-y-4">
            <h2 className="font-serif text-xl">{s.resetPassword}</h2>
            {info && <p className="text-sm text-green-700">{info}</p>}
            <Field
              label={s.verificationCode}
              value={code}
              onChange={setCode}
              type="text"
              autoFocus
              required
            />
            <Field
              label={s.newPassword}
              value={password}
              onChange={setPassword}
              type="password"
              required
              hint={s.passwordMinHint}
            />
            <Field
              label={s.passwordConfirm}
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              type="password"
              required
            />
            {error && <p className="text-sm text-red-700">{error}</p>}
            <Submit busy={busy} label={s.resetPassword} workingLabel={s.workingLabel} />
          </form>
        )}

        {mode === "passwordChanged" && (
          <div className="space-y-4">
            <h2 className="font-serif text-xl">{s.passwordChangedTitle}</h2>
            <p className="text-sm text-ink-muted">{s.passwordChangedBody}</p>
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                reset();
              }}
              className="px-4 py-2 rounded bg-ink text-parchment-100 hover:bg-ink-muted"
            >
              {s.backToSignIn}
            </button>
          </div>
        )}

        {mode === "newPasswordRequired" && (
          <form onSubmit={onNewPassword} className="space-y-4">
            <h2 className="font-serif text-xl">{s.newPasswordRequiredTitle}</h2>
            <p className="text-sm text-ink-muted">{s.newPasswordRequiredBody}</p>
            <Field
              label={s.newPassword}
              value={password}
              onChange={setPassword}
              type="password"
              autoFocus
              required
              hint={s.passwordMinHint}
            />
            <Field
              label={s.passwordConfirm}
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              type="password"
              required
            />
            {error && <p className="text-sm text-red-700">{error}</p>}
            <Submit busy={busy} label={s.signIn} workingLabel={s.workingLabel} />
          </form>
        )}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type: "text" | "email" | "password";
  required?: boolean;
  autoFocus?: boolean;
  hint?: string;
}

function Field({
  label,
  value,
  onChange,
  type,
  required,
  autoFocus,
  hint,
}: FieldProps): JSX.Element {
  return (
    <label className="block">
      <span className="text-sm text-ink-muted block mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoComplete={
          type === "email" ? "email" : type === "password" ? "current-password" : undefined
        }
        className="w-full border border-parchment-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ink"
      />
      {hint && <span className="text-xs text-ink-muted mt-1 block">{hint}</span>}
    </label>
  );
}

interface SubmitProps {
  busy: boolean;
  label: string;
  workingLabel: string;
}

function Submit({ busy, label, workingLabel }: SubmitProps): JSX.Element {
  return (
    <button
      type="submit"
      disabled={busy}
      className="w-full px-4 py-2 rounded bg-ink text-parchment-100 hover:bg-ink-muted disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy ? workingLabel : label}
    </button>
  );
}
